import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithLogout } from "@/lib/baseQuery";
import { CollectionData } from "@/lib/type";
import {
  AccessRole,
  AccessRoleFormData,
  AccessRoleScope,
  Employee,
  PermissionDomain,
} from "./permission.type";

// Builds the collection path for a scope (restaurant or franchise).
const scopePath = (scope: AccessRoleScope) =>
  scope.restaurantId
    ? `restaurants/${scope.restaurantId}/access_roles`
    : `restaurant_franchises/${scope.franchiseId}/access_roles`;

const employeesPath = (scope: AccessRoleScope) =>
  scope.restaurantId
    ? `restaurants/${scope.restaurantId}/employees`
    : `restaurant_franchises/${scope.franchiseId}/employees`;

export const AccessRoleApi = createApi({
  reducerPath: "accessRoleApi",
  tagTypes: ["access-role", "employee", "permissions", "preset"],
  baseQuery: baseQueryWithLogout,
  endpoints: (build) => ({
    // Permission catalog, grouped by domain.
    getPermissions: build.query<PermissionDomain[], void>({
      query: () => "permissions",
      transformResponse: (data: CollectionData<PermissionDomain>) =>
        data["hydra:member"] ?? [],
      providesTags: ["permissions"],
    }),

    // System presets — the shared, read-only library (Inventory Manager,
    // Cashier Supervisor, …). Each carries isSystem: true. Assign directly, or
    // clone into a scoped role via createAccessRole with the same permissions.
    getSystemPresets: build.query<AccessRole[], void>({
      query: () => "access_roles",
      transformResponse: (data: CollectionData<AccessRole>) =>
        (data["hydra:member"] ?? []).filter((r) => r.isSystem),
      providesTags: ["preset"],
    }),

    // Access roles for a scope. For a restaurant this includes inherited
    // franchise roles in addition to the restaurant's own local roles.
    getAccessRoles: build.query<AccessRole[], AccessRoleScope>({
      query: (scope) => scopePath(scope),
      transformResponse: (data: CollectionData<AccessRole>) =>
        data["hydra:member"] ?? [],
      providesTags: ["access-role"],
    }),

    getAccessRole: build.query<AccessRole, string>({
      query: (id) => `access_roles/${id}`,
      providesTags: ["access-role"],
    }),

    createAccessRole: build.mutation<
      AccessRole,
      { scope: AccessRoleScope; data: AccessRoleFormData }
    >({
      query: ({ scope, data }) => ({
        url: scopePath(scope),
        method: "POST",
        body: data,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["access-role"],
    }),

    updateAccessRole: build.mutation<
      AccessRole,
      { scope: AccessRoleScope; id: string; data: AccessRoleFormData }
    >({
      query: ({ scope, id, data }) => ({
        url: `${scopePath(scope)}/${id}`,
        method: "PATCH",
        // Stringify manually: RTK's auto-serialization doesn't recognise
        // merge-patch+json, so a raw object body would reach the API as
        // "[object Object]" and 400 with "Syntax error".
        body: JSON.stringify(data),
        headers: { "Content-Type": "application/merge-patch+json" },
      }),
      invalidatesTags: ["access-role", "employee"],
    }),

    deleteAccessRole: build.mutation<
      void,
      { scope: AccessRoleScope; id: string }
    >({
      query: ({ scope, id }) => ({
        url: `${scopePath(scope)}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["access-role", "employee"],
    }),

    // Employees with their embedded access-role assignments.
    getEmployees: build.query<Employee[], AccessRoleScope>({
      query: (scope) => employeesPath(scope),
      transformResponse: (data: CollectionData<Employee>) =>
        data["hydra:member"] ?? [],
      providesTags: ["employee"],
    }),

    getEmployee: build.query<Employee, string>({
      query: (id) => `employees/${id}`,
      providesTags: ["employee"],
    }),

    assignAccessRole: build.mutation<
      unknown,
      { employeeId: string; roleId: string }
    >({
      query: ({ employeeId, roleId }) => ({
        url: "employee_access_roles",
        method: "POST",
        body: {
          employee: `/api/employees/${employeeId}`,
          accessRole: `/api/access_roles/${roleId}`,
        },
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["employee"],
    }),

    unassignAccessRole: build.mutation<void, string>({
      query: (assignmentId) => ({
        url: `employee_access_roles/${assignmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["employee"],
    }),
  }),
});

export const {
  useGetPermissionsQuery,
  useGetSystemPresetsQuery,
  useGetAccessRolesQuery,
  useGetAccessRoleQuery,
  useCreateAccessRoleMutation,
  useUpdateAccessRoleMutation,
  useDeleteAccessRoleMutation,
  useGetEmployeesQuery,
  useGetEmployeeQuery,
  useAssignAccessRoleMutation,
  useUnassignAccessRoleMutation,
} = AccessRoleApi;
